import React, { useState, useEffect, useRef } from 'react';
import TenantLayout from '../components/TenantLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { exportToExcel, exportToPDF } from '../utils/exportUtils';
export default function ReportsDesk() {
    const { clientSlug } = useParams();
    const { api } = useAuth();
    const { t } = useLanguage();
    
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Dropdown refs for click-outside
    const typeDropdownRef = useRef(null);
    const categoryDropdownRef = useRef(null);
    
    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
                setShowTypeDropdown(false);
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // All possible categories mapped
    const ALL_CATEGORIES = [
        'sales_master', 'services', 'other_income',
        'purchase_master', 'rent', 'payroll', 'utility', 'office_supplies', 'other_expense',
        'receipt', 'payment_out'
    ];
    
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const baseYear = 2023;
    const maxYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    
    // Generate FY options from baseYear up to maxYear + 1
    const fyOptions = [];
    for (let y = baseYear; y <= maxYear + 1; y++) {
        fyOptions.push(`FY ${y}-${y + 1}`);
    }
    
    const defaultFy = currentMonth >= 3 ? `FY ${currentYear}-${currentYear + 1}` : `FY ${currentYear - 1}-${currentYear}`;
    const [fy, setFy] = useState(defaultFy);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedParty, setSelectedParty] = useState('');
    const [parties, setParties] = useState([]);
    
    // Multi-select state
    const [typeFilter, setTypeFilter] = useState(['income', 'expense']);
    const [categoryFilter, setCategoryFilter] = useState([...ALL_CATEGORIES]);
    
    // State to toggle dropdowns
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            // For MVP, we fetch all and filter on client side. 
            // In production with large data, pass filters as query params.
            const [txRes, payRes, partyRes] = await Promise.all([
                api.get(`/api/tenant/${clientSlug}/transactions`),
                api.get(`/api/tenant/${clientSlug}/payments`),
                api.get(`/api/tenant/${clientSlug}/parties`)
            ]);
            setParties(partyRes.data);
            
            // Normalize data for master ledger view
            const normalizedTx = txRes.data.map(t => ({
                ...t,
                displayType: t.type === 'income' ? 'Income' : 'Expense',
                displayColor: t.type === 'income' ? 'text-emerald-600' : 'text-rose-600',
                sign: t.type === 'income' ? 1 : -1,
                party: t.party_name || '-'
            }));
            
            const normalizedPay = payRes.data.map(p => ({
                ...p,
                description: `Settlement via ${p.payment_mode}`,
                category: p.type === 'payment_received' ? 'Receipt' : 'Payment Out',
                displayType: p.type === 'payment_received' ? 'Income' : 'Expense',
                displayColor: p.type === 'payment_received' ? 'text-emerald-600' : 'text-rose-600',
                sign: p.type === 'payment_received' ? 1 : -1,
                party: p.party_name
            }));

            const combined = [...normalizedTx, ...normalizedPay].sort((a, b) => new Date(b.date) - new Date(a.date));
            setTransactions(combined);
        } catch (err) {
            console.error(err);
            toast.error(t("Failed to load reports data"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [clientSlug]);

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val);

    // Naive filtering logic for UI demonstration
    const filteredData = transactions.filter(t => {
        // 1. Date Range Filter
        if (startDate) {
            if (new Date(t.date) < new Date(startDate)) return false;
        }
        if (endDate) {
            const endD = new Date(endDate);
            endD.setHours(23, 59, 59, 999);
            if (new Date(t.date) > endD) return false;
        }
        
        // 2. Type Filter (Multi-select)
        const typeKey = t.displayType.toLowerCase().replace(' ', '_');
        if (!typeFilter.includes(typeKey)) {
            return false;
        }
        
        // 3. Category Filter (Multi-select)
        const catKey = t.category.toLowerCase().replace(/ /g, '_');
        if (!categoryFilter.includes(catKey)) {
            return false;
        }

        // 4. Party Filter
        // Note: For income/expense transactions, the party field is hardcoded to '-',
        // and we have party_id / party_name available on the backend but they might not be passed or mapped correctly to 'party' above.
        // Wait, on line 76 of ReportsDesk it says `party: '-'`. This makes the party filter fail for Income/Expense.
        // I will fix `party` property to be `t.party_name || '-'` instead.
        if (selectedParty && t.party !== selectedParty) {
            return false;
        }

        // 5. Month Filter
        if (selectedMonth !== '') {
            if (new Date(t.date).getMonth() !== parseInt(selectedMonth)) return false;
        }

        // 6. FY Filter (April to March)
        if (fy) {
            // handle both "FY 2023-2024" and "FY 2023-24" formats
            const cleanFy = fy.replace('FY ', '').trim();
            const parts = cleanFy.split('-');
            if (parts.length === 2) {
                const startYear = parseInt(parts[0]);
                let endYear = parseInt(parts[1]);
                // If endYear is "24", parse it as 2024
                if (endYear < 100) endYear += 2000;
                
                const fyStart = new Date(startYear, 3, 1);
                const fyEnd = new Date(endYear, 2, 31, 23, 59, 59);
                const tDate = new Date(t.date);
                if (tDate < fyStart || tDate > fyEnd) return false;
            }
        }
        
        return true;
    });

    const totalIn = filteredData.filter(t => t.sign > 0).reduce((acc, t) => acc + t.amount, 0);
    const totalOut = filteredData.filter(t => t.sign < 0).reduce((acc, t) => acc + t.amount, 0);
    const netProfit = totalIn - totalOut;

    const toggleType = (typeVal) => {
        if (typeFilter.includes(typeVal)) {
            setTypeFilter(typeFilter.filter(t => t !== typeVal));
        } else {
            setTypeFilter([...typeFilter, typeVal]);
        }
    };

    const toggleCategory = (catVal) => {
        if (categoryFilter.includes(catVal)) {
            setCategoryFilter(categoryFilter.filter(c => c !== catVal));
        } else {
            setCategoryFilter([...categoryFilter, catVal]);
        }
    };

    const handleExportCSV = () => {
        toast.info(t("Generating CSV..."));
        const headers = ["Date", "Type", "Description", "Party", "Category", "Mode", "Amount", "GST Slab", "Taxable Amount", "Grand Total"];
        
        let csvRows = [];
        // Header row
        csvRows.push(headers.join(','));
        
        const padDate = (dateString) => {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const [day, month, year] = parts;
                return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
            }
            return dateString;
        };

        filteredData.forEach(row => {
            let rawDateStr = new Date(row.date).toLocaleDateString('en-IN');
            let date = padDate(rawDateStr);
            let pMode = row.payment_mode || 'Cash';
            
            if (row.line_items && row.line_items.length > 0) {
                // For itemized invoices, output each line item as a row
                row.line_items.forEach(item => {
                    const rowData = [
                        date,
                        row.displayType,
                        `"${item.item.replace(/"/g, '""')}"`,
                        `"${(row.party_name || row.party).replace(/"/g, '""')}"`,
                        row.category,
                        pMode,
                        row.sign > 0 ? item.total_amount : -item.total_amount,
                        `${item.gst_slab}%`,
                        item.taxable_amount,
                        row.sign > 0 ? row.amount : -row.amount
                    ];
                    csvRows.push(rowData.join(','));
                });
            } else {
                // Fallback for non-itemized transactions or simple payments
                const amount = row.sign > 0 ? row.amount : -row.amount;
                const rowData = [
                    date,
                    row.displayType,
                    `"${row.description.replace(/"/g, '""')}"`,
                    `"${(row.party_name || row.party).replace(/"/g, '""')}"`,
                    row.category,
                    pMode,
                    amount,
                    "N/A",
                    amount,
                    amount
                ];
                csvRows.push(rowData.join(','));
            }
        });
        
        // Add Summary Rows
        csvRows.push(''); // Empty line
        csvRows.push('SUMMARY,,,,,');
        csvRows.push(`Total Income,,,,,,${totalIn.toFixed(2)},,,`);
        csvRows.push(`Total Expense,,,,,,${totalOut.toFixed(2)},,,`);
        csvRows.push(`Net Profit,,,,,,${netProfit.toFixed(2)},,,`);
        
        const csvContent = "\uFEFF" + csvRows.join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `${clientSlug}_ledger_export.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    const handleExportPDF = () => {
        toast.info(t("Generating PDF..."));
        const exportColumns = [
            { header: 'Date', dataKey: (row) => row.date ? new Date(row.date).toLocaleDateString('en-IN') : '' },
            { header: 'Type', dataKey: (row) => t(row.displayType || '') },
            { header: 'Description', dataKey: 'description' },
            { header: 'Party', dataKey: (row) => (row.party_name || (row.party && row.party !== '-' ? row.party : '')) },
            { header: 'Category', dataKey: (row) => t((row.category || '').replace('_', ' ').toUpperCase()) },
            { header: 'Mode', dataKey: (row) => t(row.payment_mode || (row.date ? 'Cash' : '')) },
            { header: 'Amount', dataKey: (row) => row.isSummary ? row.amount : (row.amount === undefined ? '' : `${row.sign > 0 ? '+' : '-'}${row.amount}`) }
        ];
        
        let title = "Ledger Report";
        if (selectedParty) title = `${selectedParty} - Ledger Report`;
        
        const summaryData = [
            { isSummary: true, date: '', displayType: '', description: '', party_name: '', category: 'Total Income', payment_mode: '', amount: `+${totalIn.toFixed(2)}`, sign: 1 },
            { isSummary: true, date: '', displayType: '', description: '', party_name: '', category: 'Total Expense', payment_mode: '', amount: `-${totalOut.toFixed(2)}`, sign: -1 },
            { isSummary: true, date: '', displayType: '', description: '', party_name: '', category: 'Net Profit', payment_mode: '', amount: `${netProfit >= 0 ? '+' : ''}${netProfit.toFixed(2)}`, sign: netProfit >= 0 ? 1 : -1 }
        ];

        exportToPDF([...filteredData, {}, ...summaryData], `${clientSlug}_ledger_export`, exportColumns, title);
        toast.success(t("PDF exported successfully!"));
    };

    const handleExportExcel = () => {
        const exportColumns = [
            { header: 'Date', dataKey: (row) => row.date ? new Date(row.date).toLocaleDateString('en-IN') : '' },
            { header: 'Type', dataKey: (row) => t(row.displayType || '') },
            { header: 'Description', dataKey: 'description' },
            { header: 'Party', dataKey: (row) => (row.party_name || (row.party && row.party !== '-' ? row.party : '')) },
            { header: 'Category', dataKey: (row) => t((row.category || '').replace('_', ' ').toUpperCase()) },
            { header: 'Mode', dataKey: (row) => t(row.payment_mode || (row.date ? 'Cash' : '')) },
            { header: 'Amount', dataKey: (row) => row.isSummary ? row.amount : (row.amount === undefined ? '' : `${row.sign > 0 ? '+' : '-'}${row.amount}`) }
        ];
        
        let title = "Ledger Report";
        if (selectedParty) title = `${selectedParty} - Ledger Report`;

        const summaryData = [
            { isSummary: true, date: '', displayType: '', description: '', party_name: '', category: 'Total Income', payment_mode: '', amount: `+${totalIn.toFixed(2)}`, sign: 1 },
            { isSummary: true, date: '', displayType: '', description: '', party_name: '', category: 'Total Expense', payment_mode: '', amount: `-${totalOut.toFixed(2)}`, sign: -1 },
            { isSummary: true, date: '', displayType: '', description: '', party_name: '', category: 'Net Profit', payment_mode: '', amount: `${netProfit >= 0 ? '+' : ''}${netProfit.toFixed(2)}`, sign: netProfit >= 0 ? 1 : -1 }
        ];

        exportToExcel([...filteredData, {}, ...summaryData], `${clientSlug}_ledger_export`, exportColumns);
        toast.success(t("Excel exported successfully!"));
    };

    return (
        <TenantLayout title={t("Reports & Reconciliation")} tagline={t("Dynamic ledger history & export center")}>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 mb-6">
                 {/* Filters */}
                 <div className="flex flex-wrap gap-4 items-start mb-6 border-b border-slate-100 pb-6 relative">
                     <div>
                         <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Financial Year")}</label>
                         <select 
                            data-testid="reports-fy-select"
                            value={fy} 
                            onChange={e => {
                                setFy(e.target.value);
                            }}
                            className="w-full h-12 sm:h-10 px-3 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-[#EAB308] outline-none"
                         >
                             <option value="">{t("All FY")}</option>
                             {fyOptions.map(option => (
                                 <option key={option} value={option}>{option}</option>
                             ))}
                         </select>
                     </div>

                     <div>
                         <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Month")}</label>
                         <select 
                            value={selectedMonth} 
                            onChange={e => setSelectedMonth(e.target.value)}
                            className="w-full h-12 sm:h-10 px-3 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-[#EAB308] outline-none"
                         >
                            <option value="">{t("All Months")}</option>
                            <option value="0">{t("January")}</option>
                            <option value="1">{t("February")}</option>
                            <option value="2">{t("March")}</option>
                            <option value="3">{t("April")}</option>
                            <option value="4">{t("May")}</option>
                            <option value="5">{t("June")}</option>
                            <option value="6">{t("July")}</option>
                            <option value="7">{t("August")}</option>
                            <option value="8">{t("September")}</option>
                            <option value="9">{t("October")}</option>
                            <option value="10">{t("November")}</option>
                            <option value="11">{t("December")}</option>
                         </select>
                     </div>

                     <div>
                         <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Start Date")}</label>
                         <input 
                            type="date"
                            data-testid="reports-start-date-input"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full h-12 sm:h-10 px-3 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-[#EAB308] outline-none"
                         />
                     </div>

                     <div>
                         <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("End Date")}</label>
                         <input 
                            type="date"
                            data-testid="reports-end-date-input"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full h-12 sm:h-10 px-3 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-[#EAB308] outline-none"
                         />
                     </div>
                     
                     <div className="w-full sm:w-auto">
                         <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Party")}</label>
                         <select 
                            value={selectedParty} 
                            onChange={e => setSelectedParty(e.target.value)}
                            className="w-full h-12 sm:h-10 px-3 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-[#EAB308] outline-none"
                         >
                            <option value="">{t("All Parties")}</option>
                            {[...new Set(parties.map(p => p.name))].map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                         </select>
                     </div>

                     {/* Custom Multi-Select for Type */}
                     <div className="relative" ref={typeDropdownRef}>
                         <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Types")}</label>
                         <div 
                             data-testid="type-multiselect-trigger"
                             onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                             className="h-10 px-3 min-w-[140px] rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium flex items-center justify-between cursor-pointer"
                         >
                             <span>{typeFilter.length === 2 ? t('All Types') : `${typeFilter.length} ${t('Selected')}`}</span>
                             <span className="text-xs ml-2">▼</span>
                         </div>
                         {showTypeDropdown && (
                             <div className="absolute top-16 left-0 w-48 bg-white border border-slate-200 shadow-xl rounded-lg z-10 p-2 flex flex-col gap-2">
                                 {['income', 'expense'].map(tType => (
                                     <label key={tType} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                                         <input type="checkbox" data-testid={`type-checkbox-${tType}`} checked={typeFilter.includes(tType)} onChange={() => toggleType(tType)} className="rounded text-[#0A192F]" />
                                         <span className="capitalize">{t(tType.replace('_', ' ').toUpperCase())}</span>
                                     </label>
                                 ))}
                             </div>
                         )}
                     </div>

                     {/* Custom Multi-Select for Category */}
                     <div className="relative" ref={categoryDropdownRef}>
                         <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t("Categories")}</label>
                         <div 
                             data-testid="category-multiselect-trigger"
                             onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                             className="h-10 px-3 min-w-[150px] rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium flex items-center justify-between cursor-pointer"
                         >
                             <span>{categoryFilter.length === ALL_CATEGORIES.length ? t('All Categories') : `${categoryFilter.length} ${t('Selected')}`}</span>
                             <span className="text-xs ml-2">▼</span>
                         </div>
                         {showCategoryDropdown && (
                             <div className="absolute top-16 left-0 w-56 bg-white border border-slate-200 shadow-xl rounded-lg z-10 p-2 max-h-[300px] overflow-y-auto">
                                 <div className="flex flex-col gap-2">
                                     <button 
                                        data-testid="deselect-all-categories-button"
                                        onClick={() => setCategoryFilter(categoryFilter.length === ALL_CATEGORIES.length ? [] : [...ALL_CATEGORIES])}
                                        className="text-xs text-left font-bold text-[#0A192F] mb-1 hover:underline"
                                     >
                                         {categoryFilter.length === ALL_CATEGORIES.length ? t('Deselect All') : t('Select All')}
                                     </button>
                                     {ALL_CATEGORIES.map(c => (
                                     <label key={c} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                                         <input type="checkbox" data-testid={`category-checkbox-${c}`} checked={categoryFilter.includes(c)} onChange={() => toggleCategory(c)} className="rounded text-[#0A192F]" />
                                         <span className="capitalize text-slate-700">{t(c.replace('_', ' ').toUpperCase())}</span>
                                     </label>
                                     ))}
                                 </div>
                             </div>
                         )}
                     </div>

                     <div className="w-full sm:w-auto ml-auto flex flex-col sm:flex-row gap-3 mt-6 sm:mt-0">
                         <button 
                            onClick={handleExportExcel}
                            data-testid="reports-export-excel-button"
                            className="w-full sm:w-auto h-12 sm:h-10 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2"
                         >
                             {t("Export Excel")}
                         </button>
                         <button 
                            onClick={handleExportPDF}
                            data-testid="reports-export-pdf-button"
                            className="w-full sm:w-auto h-12 sm:h-10 px-4 rounded-lg bg-[#0A192F] hover:bg-[#0F284E] text-white text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2"
                         >
                             {t("Export PDF")}
                         </button>
                     </div>
                 </div>

                 {/* Summary Strip */}
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                     <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                         <div className="text-xs text-slate-500 font-medium mb-1">{t("Period Inflow")}</div>
                         <div data-testid="period-inflow" className="text-lg font-mono font-bold text-emerald-600">{formatCurrency(totalIn)}</div>
                     </div>
                     <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                         <div className="text-xs text-slate-500 font-medium mb-1">{t("Period Outflow")}</div>
                         <div data-testid="period-outflow" className="text-lg font-mono font-bold text-rose-600">{formatCurrency(totalOut)}</div>
                     </div>
                     <div className="bg-amber-50/50 p-4 rounded-lg border border-amber-100 md:col-span-2">
                         <div className="text-xs text-amber-800 font-medium mb-1">{t("Net Period Balance")}</div>
                         <div data-testid="net-period-balance" className={`text-xl font-mono font-bold ${totalIn - totalOut >= 0 ? 'text-[#0F284E]' : 'text-rose-600'}`}>
                             {formatCurrency(totalIn - totalOut)}
                         </div>
                     </div>
                 </div>

                 {/* Table */}
                 <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="reports-table-container">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left whitespace-nowrap min-w-[800px]">
                            <thead className="bg-[#0A192F] text-white text-xs uppercase font-mono tracking-wider">
                                <tr>
                                    <th className="px-4 py-3">{t("Date")}</th>
                                    <th className="px-4 py-3">{t("Type")}</th>
                                    <th className="px-4 py-3 min-w-[200px]">{t("Description")}</th>
                                    <th className="px-4 py-3">{t("Party Name")}</th>
                                    <th className="px-4 py-3">{t("Category")}</th>
                                    <th className="px-4 py-3">{t("Mode")}</th>
                                    <th className="px-4 py-3 text-right">{t("Amount (₹)")}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 bg-white">
                                {loading ? (
                                    <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">{t("Loading ledger data...")}</td></tr>
                                ) : filteredData.length === 0 ? (
                                    <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">{t("No records found for selected filters.")}</td></tr>
                                ) : (
                                    filteredData.map((row, i) => (
                                        <tr key={i} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap text-slate-600 text-xs">
                                                {new Date(row.date).toLocaleDateString('en-IN')}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${
                                                    row.sign > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'
                                                }`}>
                                                    {t(row.displayType)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-slate-900">
                                                <div className="font-medium">{row.description}</div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {(row.party_name || (row.party !== '-' ? row.party : 'N/A'))}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-600">
                                                {t(row.category.replace('_', ' ').toUpperCase())}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-600 font-semibold">
                                                {t(row.payment_mode || 'Cash')}
                                            </td>
                                            <td className={`px-4 py-3 text-right font-mono font-semibold ${row.displayColor}`}>
                                                {row.sign > 0 ? '+' : '-'}{formatCurrency(row.amount)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                 </div>
            </div>
        </TenantLayout>
    );
}
